import _isEqual from "lodash/isEqual";
import QueryString from "query-string";
import Cookie from "js-cookie";
import { Repo, buildPath, LingoError, ItemType, Space } from "@thenounproject/lingo-core";

import { fetchItem } from "./assets";
import { fetchSection, fetchKit, fetchKitOutline } from "./actions";
import { fetchUser } from "@queries/useFetchUser";
import { fetchSpace } from "@queries/useFetchSpace";
import { fetchAsset } from "@queries/useFetchAsset";
import * as actionTypes from "./actionTypes";

import {
  canUseSavedViews,
  canViewAssetLibrary,
  canViewCustomFields,
} from "@features/library/utils";
import { fetchKitCollection } from "@queries/useFetchKitCollection";
import { fetchCustomFields } from "@actions/useCustomFieldsFetch";
import { fetchAssetViews } from "@actions/useAssetViewsFetch";

export type NavPoint = {
  type: NavPointTypes;
  loaded: boolean;
  spaceId: number;
  space: Space | null;
  userFetched: boolean;
  spaceFetched: boolean;
  version?: number;
  role?: string;
  kitId?: string;
  assetId?: string;
  sectionId?: string;
  itemId?: string;
  insightsPage?: string;
  galleryContext?: string;
};

export enum NavPointTypes {
  "LibraryAsset" = "LibraryAsset",
  "Item" = "Item",
  "Section" = "Section",
  "Kit" = "Kit",
  "KitVersions" = "KitVersions",
  "KitTrashedItems" = "KitTrashedItems",
  "UserSettings" = "UserSettings",
  "SpaceBilling" = "SpaceBilling",
  "SpaceSearch" = "SpaceSearch",
  "SpaceSettings" = "SpaceSettings",
  "SpaceMembers" = "SpaceMembers",
  "Insights" = "Insights",
  "NewSpace" = "NewSpace",
  "SpaceKits" = "SpaceKits",
  "KitCollection" = "KitCollection",
  "DefaultSpace" = "DefaultSpace",
  "Library" = "Library",
  "TagManagement" = "TagManagement",
  "CustomFields" = "CustomFields",
  "KitRecoveredAssets" = "KitRecoveredAssets",
}

export const setNavPoint = navPoint => (dispatch, getState) => {
  if (navPoint.spaceId) {
    const repo = new Repo(getState());
    const role = repo.getSpace(navPoint.spaceId)?.access?.role;
    if (role && role !== "guest") {
      const env = _lingoConfig.environment,
        key = env === "production" ? "lastSpace" : `lastSpace.${env}`;
      Cookie.set(key, navPoint.spaceId, { expires: 365, domain: "lingoapp.com" });
    }
  }

  return dispatch({
    type: actionTypes.SET_NAV_POINT,
    navPoint,
  });
};

// MARK : Fetchers
// -------------------------------------------------------------------------------

const fetchUserIfNeeded = navPoint => (dispatch, getState) => {
  if (navPoint.userFetched) return {};

  const res = { userFetched: true },
    {
      user: { isFetched },
    } = getState();
  if (isFetched) return Promise.resolve(res);
  return (
    dispatch(fetchUser())
      .then(({ type, error }) => {
        if (type === actionTypes.FETCH_USER_FAILURE) {
          throw new LingoError(error);
        }
        return res;
      })
      // eslint-disable-next-line handle-callback-err
      .catch(() => {
        return res;
      })
  );
};

export const fetchSpaceIfNeeded = navPoint => (dispatch, getState) => {
  if (navPoint.spaceFetched || !(navPoint.spaceId || navPoint.subdomain)) {
    return {};
  }

  function fetch() {
    const { spaceId, subdomain } = navPoint,
      repo = new Repo(getState()),
      cached = repo.getSpace(spaceId || subdomain);

    if (cached) return Promise.resolve(cached);

    return dispatch(fetchSpace(spaceId || subdomain)).then(actionResult => {
      const { type, response, error } = actionResult;
      if (type === actionTypes.FETCH_SPACE_FAILURE) {
        if (navPoint.spaceOptional) {
          return (error.details || {}).space_id ? { id: error.details.space_id } : null;
        }
        throw new LingoError(error);
      } else if (type === actionTypes.FETCH_SPACE_SUCCESS && response) {
        return ((response.entities || {}).spaces || {})[response.result];
      }
      return null;
    });
  }

  return fetch().then(space =>
    space ? { spaceId: space.id, spaceFetched: true } : { spaceFetched: true }
  );
};

export const fetchKitCollectionIfNeeded = navPoint => (dispatch, getState) => {
  if (!navPoint.kitCollectionId) return {};
  if (navPoint.kitCollectionFetched || !(navPoint.spaceId || navPoint.subdomain)) {
    return {};
  }

  function fetch() {
    const { kitCollectionId } = navPoint,
      repo = new Repo(getState()),
      cached = repo.getKitCollection(kitCollectionId);

    if (cached) return Promise.resolve(cached);

    return dispatch(fetchKitCollection(kitCollectionId)).then(actionResult => {
      const { type, response, error } = actionResult;
      if (type === actionTypes.FETCH_KIT_COLLECTION_FAILURE) {
        throw new LingoError(error);
      } else if (type === actionTypes.FETCH_KIT_COLLECTION_SUCCESS && response) {
        return ((response.entities || {}).kitCollections || {})[response.result];
      }
      return null;
    });
  }

  return fetch().then(kitCollection =>
    kitCollection
      ? { kitCollectionId: kitCollection.uuid, kitCollectionFetched: true }
      : { kitCollectionFetched: true }
  );
};

export const fetchAssetViewsIfNeeded = navPoint => (dispatch, getState) => {
  if (navPoint.assetViewsFetched || !(navPoint.spaceId || navPoint.subdomain)) {
    return {};
  }

  const repo = new Repo(getState()),
    space = repo.getSpace(navPoint.spaceId || navPoint.subdomain);

  if (!space) return {};
  if (!canViewAssetLibrary(space) || !canUseSavedViews(space)) return { assetViewsFetched: true };

  function fetch() {
    return dispatch(
      fetchAssetViews({
        spaceId: space.id,
      })
    ).then(actionResult => {
      const { type, response, error } = actionResult;
      if (type === actionTypes.FETCH_ASSET_VIEWS_FAILURE) {
        throw new LingoError(error);
      } else if (type === actionTypes.FETCH_ASSET_VIEWS_SUCCESS && response) {
        return ((response.entities || {}).views || {})[response.result];
      }
      return null;
    });
  }

  return fetch().then(() => {
    return { assetViewsFetched: true };
  });
};

export const fetchCustomFieldsIfNeeded = navPoint => (dispatch, getState) => {
  if (navPoint.customFieldsFetched || !(navPoint.spaceId || navPoint.subdomain)) {
    return {};
  }

  const repo = new Repo(getState()),
    space = repo.getSpace(navPoint.spaceId || navPoint.subdomain);

  if (!space) return {};
  if (!canViewCustomFields(space)) return { customFieldsFetched: true };

  function fetch() {
    return dispatch(
      fetchCustomFields({
        spaceId: space.id,
        kitUuid: navPoint.kitId,
        assetId: navPoint.assetId,
        assetToken: navPoint.assetToken,
      })
    ).then(actionResult => {
      const { type, response, error } = actionResult;
      if (type === actionTypes.FETCH_CUSTOM_FIELDS_FAILURE) {
        throw new LingoError(error);
      } else if (type === actionTypes.FETCH_CUSTOM_FIELDS_SUCCESS && response) {
        return ((response.entities || {}).fields || {})[response.result];
      }
      return null;
    });
  }

  return fetch().then(() => {
    return { customFieldsFetched: true };
  });
};

export const fetchItemIfNeeded = navPoint => (dispatch, getState) => {
  if (navPoint.itemFetched || !navPoint.itemId) return {};
  if (!navPoint.spaceId && !navPoint.subdomain) return {};

  function fetch() {
    const { spaceId, subdomain, itemId, version, assetToken } = navPoint,
      repo = new Repo(getState()),
      cached = repo.getItem(itemId, version);

    if (cached) return Promise.resolve(cached);

    const action = fetchItem(spaceId || subdomain, itemId, version, assetToken);
    return dispatch(action).then(actionResult => {
      const { type, response, error } = actionResult;
      if (type === actionTypes.FETCH_ITEM_FAILURE) {
        if (error.details && error.details.access === "password" && !navPoint.kitId) {
          return { kitId: error.details.kit_uuid, retry: true };
        }
        throw new LingoError(error);
      } else if (type === actionTypes.FETCH_ITEM_SUCCESS && response) {
        return ((response.entities || {}).items || {})[response.result];
      }
      return null;
    });
  }
  return fetch().then(item => {
    if (!item) return { itemFetched: true };
    return item.retry
      ? { kitId: item.kitId }
      : {
          itemId: item.id,
          sectionId: item.sectionId,
          kitId: item.kitId,
          version: item.version,
          assetId: item.assetId,
          assetToken: item.asset?.shareToken,
          itemFetched: true,
          assetFetched: Boolean(item.asset),
        };
  });
};

export const fetchAssetIfNeeded = navPoint => (dispatch, getState) => {
  if (navPoint.assetFetched || !navPoint.assetId) return {};
  if (!navPoint.spaceId && !navPoint.subdomain) return {};

  function fetch() {
    const { assetId, assetToken } = navPoint,
      repo = new Repo(getState()),
      cached = repo.getAsset(assetId);

    if (cached) return Promise.resolve(cached);

    const action = fetchAsset(assetId, assetToken);
    return dispatch(action).then(actionResult => {
      const { type, response, error } = actionResult;
      if (type === actionTypes.FETCH_ASSET_FAILURE) {
        throw new LingoError(error);
      } else if (type === actionTypes.FETCH_ASSET_SUCCESS && response) {
        return ((response.entities || {}).assets || {})[response.result];
      }
      return null;
    });
  }
  return fetch().then(asset => {
    if (!asset) return { assetFetched: true };
    return {
      assetToken: asset.shareToken,
      assetFetched: true,
    };
  });
};

export const fetchSectionIfNeeded = navPoint => (dispatch, getState) => {
  if (navPoint.sectionFetched || !navPoint.sectionId) return {};
  if (!navPoint.spaceId && !navPoint.subdomain) return {};

  function fetch() {
    const { spaceId, subdomain, sectionId, version } = navPoint,
      repo = new Repo(getState()),
      cached = repo.getSection(sectionId, version);

    // We need counts to display a section
    if (cached && cached.counts) return Promise.resolve(cached);

    const action = fetchSection(spaceId || subdomain, sectionId, version, 1);
    return dispatch(action).then(actionResult => {
      const { type, response, error } = actionResult;
      if (type === actionTypes.FETCH_SECTION_FAILURE) {
        if (error.details && error.details.access === "password" && !navPoint.kitId) {
          return { kitId: error.details.kit_uuid, retry: true };
        }
        if (navPoint.sectionOptional) return null;
        throw new LingoError(error);
      } else if (type === actionTypes.FETCH_SECTION_SUCCESS && response) {
        return ((response.entities || {}).sections || {})[response.result];
      }
      return null;
    });
  }

  return fetch().then(section =>
    section
      ? section.retry
        ? { kitId: section.kitId }
        : {
            sectionId: section.id,
            kitId: section.kitId,
            version: section.version,
            sectionFetched: true,
          }
      : { sectionFetched: true }
  );
};

export const fetchKitIfNeeded = navPoint => (dispatch, getState) => {
  if (navPoint.kitFetched || !navPoint.kitId) return {};
  if (!navPoint.spaceId && !navPoint.subdomain) return {};

  function fetch() {
    const { spaceId, subdomain, kitId } = navPoint,
      repo = new Repo(getState()),
      cached = repo.getKit(kitId, spaceId);

    if (cached && cached.versions && cached.versions.length) return Promise.resolve(cached);

    return dispatch(fetchKit(kitId, spaceId || subdomain)).then(actionResult => {
      const { type, response, error } = actionResult;
      if (type === actionTypes.FETCH_KIT_FAILURE) {
        if (navPoint.kitOptional) return null;
        throw new LingoError(error);
      } else if (type === actionTypes.FETCH_KIT_SUCCESS && response) {
        return ((response.entities || {}).kits || {})[response.result];
      }
      return null;
    });
  }

  return fetch().then(kit => {
    const res: Record<string, any> = { kitFetched: true };
    if (kit) {
      res.kitId = kit.kitId;
    } else if (navPoint.type === "Item") {
      res.public = true;
    }
    return res;
  });
};

export const fetchKitOutlineIfNeeded = navPoint => (dispatch, getState) => {
  if (navPoint.kitVersionFetched || !navPoint.kitId) return {};
  if (!navPoint.spaceId && !navPoint.subdomain) return {};

  function fetch() {
    const { spaceId, subdomain, kitId, version } = navPoint,
      repo = new Repo(getState()),
      cached = repo.getVersion(kitId, version);

    if (cached && cached.sections) return Promise.resolve(cached);

    const action = fetchKitOutline(spaceId || subdomain, kitId, version);
    return dispatch(action).then(actionResult => {
      const { type, response, error } = actionResult;
      if (type === actionTypes.FETCH_KIT_OUTLINE_FAILURE) {
        if (navPoint.kitVersionOptional) return null;
        throw new LingoError(error);
      } else if (type === actionTypes.FETCH_KIT_OUTLINE_SUCCESS && response) {
        return ((response.entities || {}).kitVersions || {})[response.result];
      }
      return null;
    });
  }
  return fetch().then(
    kitVersion =>
      kitVersion
        ? { version: kitVersion.version, kitVersionFetched: true }
        : { kitVersionFetched: true, public: navPoint.kitVersionOptional }
    // If we don't get a version, consider the navpoint public if we didn't need it
  );
};

// MARK : Population
/* ------------------------------------------------------------------------------- */

function isNavPointLoaded(navPoint) {
  const needsLoad =
    ((!navPoint.kitFetched || !navPoint.kitVersionFetched) && navPoint.kitId) ||
    (!navPoint.itemFetched && navPoint.itemId) ||
    (!navPoint.sectionFetched && navPoint.sectionId) ||
    (!navPoint.spaceFetched && (navPoint.spaceId || navPoint.subdomain)) ||
    (!navPoint.customFieldsFetched && navPoint.spaceFetched) ||
    (!navPoint.assetViewsFetched && navPoint.spaceFetched) ||
    !navPoint.userFetched;
  return !needsLoad;
}

const fetchNavPoint =
  (navPoint, i = 0) =>
  dispatch => {
    if (i > 5) return navPoint;
    else if (!isNavPointLoaded(navPoint)) {
      return Promise.all([
        dispatch(fetchUserIfNeeded(navPoint)),
        dispatch(fetchSpaceIfNeeded(navPoint)),
        dispatch(fetchItemIfNeeded(navPoint)),
        dispatch(fetchAssetIfNeeded(navPoint)),
        dispatch(fetchSectionIfNeeded(navPoint)),
        dispatch(fetchKitIfNeeded(navPoint)),
        dispatch(fetchKitOutlineIfNeeded(navPoint)),
        dispatch(fetchKitCollectionIfNeeded(navPoint)),
        dispatch(fetchCustomFieldsIfNeeded(navPoint)),
        dispatch(fetchAssetViewsIfNeeded(navPoint)),
      ]).then(entities => {
        const improvedNavPoint = entities.reduce((consolidated, entityNavPoint) => {
          Object.keys(consolidated).forEach(key => {
            if (
              (entityNavPoint[key] === undefined || entityNavPoint[key] === null) &&
              consolidated[key] !== undefined
            ) {
              delete entityNavPoint[key];
            }
          });
          return { ...consolidated, ...entityNavPoint, loaded: false };
        }, navPoint);

        return dispatch(fetchNavPoint(improvedNavPoint, i + 1));
      });
    } else {
      return Promise.resolve({ ...navPoint, loaded: true });
    }
  };

export const loadNavPoint = (navPoint, history) => (dispatch, getState) => {
  return dispatch(fetchNavPoint(navPoint, 0))
    .then(completeNavPoint => {
      dispatch(setNavPoint(completeNavPoint));
      return completeNavPoint;
    })
    .catch(err => {
      if (!LingoError.isError(err)) {
        throw err;
      }

      if (err.details && err.details.access === "password") {
        const target = window.location.toString();
        const queryString = QueryString.stringify({
          next: target,
          kitId: err.details.kit_uuid || undefined,
        });
        history.replace(`/enter-password/?${queryString}`);
      } else if (getState().user.id || err.code === LingoError.Code.serviceUnavailable) {
        dispatch(setNavPoint({ ...navPoint, loaded: true, failed: true, error: err }));
      } else {
        const target = window.location.toString();
        history.replace(`/login/?next=${target}`);
      }
    });
};

// MARK : Getters
/* ------------------------------------------------------------------------------- */

export function getPopulatedNavPoint(navPoint, state) {
  const repo = new Repo(state);

  const space = repo.getSpace(navPoint.spaceId),
    kit = repo.getKit(navPoint.kitId, navPoint.spaceId),
    kitVersion = repo.getVersion(navPoint.kitId, navPoint.version),
    item = repo.getItem(navPoint.itemId, navPoint.version),
    section = repo.getSection(navPoint.sectionId, navPoint.version);

  return {
    ...navPoint,
    space,
    item,
    section,
    kit: _isEqual(kit, { deleted: true }) ? null : kit,
    kitVersion,
  };
}

export const getItemUrl = context => item => {
  if (!item) return null;
  const { spaceId, sectionId, galleryContext } = context,
    space = { id: spaceId },
    currentParams = QueryString.parse(window.location.search),
    { type: itemType, shortId, version, asset } = item;

  const { shareToken } = asset || {};

  const params = {
    v: version,
    asset_token: shareToken || undefined,
    kit_token: currentParams.tkn || currentParams.kit_token || undefined,
    previous: currentParams.previous || undefined,
    context: undefined,
  };

  if (itemType !== ItemType.asset) {
    return buildPath(`/s/${sectionId}`, { space }, params, shortId);
  }
  if (galleryContext) params.context = galleryContext;

  return buildPath(`/a/${shortId}`, { space }, params);
};

// MARK : Creators
/* ------------------------------------------------------------------------------- */

function isUUID(id) {
  const uuidPattern = "^(X{8}-X{4}-4X{3}-[89abAB]X{3}-X{12})".replace(/X/g, "[0-9A-F]");
  const match = id.match(uuidPattern) || [];
  return match && match[1];
}

function parseIdentifier(id) {
  if (!id) return id;
  if (isUUID(id)) return id;
  return id.split("-").pop();
}

export function createItemNavPoint(match, query, subdomain) {
  const { spaceId, itemUuid } = match.params,
    version = query.v,
    search = query.q,
    galleryContext = query.context,
    assetToken = query.asset_token;
  return {
    type: NavPointTypes.Item,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    itemId: parseIdentifier(itemUuid),
    assetToken,
    version,
    search,
    galleryContext,
    item: null,
    kit: null,
    kitOptional: true,
    kitVersion: null,
    kitVersionOptional: true,
    section: null,
    sectionOptional: true,
    space: null,
    spaceOptional: true,
  };
}

export function createSectionNavPoint(match, query, subdomain) {
  const { spaceId, sectionUuid } = match.params,
    version = query.v;
  return {
    type: NavPointTypes.Section,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    sectionId: parseIdentifier(sectionUuid),
    version,
    kit: null,
    kitVersion: null,
    section: null,
    space: null,
    spaceOptional: true,
  };
}

export function createKitNavPoint(match, query, subdomain) {
  const { spaceId, kitUuid } = match.params,
    version = query.v,
    search = query.q;
  return {
    type: NavPointTypes.Kit,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    kitId: parseIdentifier(kitUuid),
    version,
    search,
    kit: null,
    kitVersion: null,
    space: null,
    spaceOptional: true,
  };
}

export function createKitVersionsNavPoint(match, query, subdomain) {
  const { spaceId, kitUuid } = match.params;
  return {
    type: NavPointTypes.KitVersions,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    kitId: parseIdentifier(kitUuid),
    kit: null,
    kitVersion: null,
    space: null,
    spaceOptional: true,
  };
}

export function createKitTrashedItemsNavPoint(match, query, subdomain) {
  const { spaceId, kitUuid } = match.params;
  return {
    type: NavPointTypes.KitTrashedItems,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    kitId: parseIdentifier(kitUuid),
    version: 0,
    kit: null,
    kitVersion: null,
    space: null,
    spaceOptional: true,
  };
}

export function createKitRecoveredAssetsNavPoint(match, query, subdomain) {
  const { spaceId, kitUuid } = match.params;
  return {
    type: NavPointTypes.KitRecoveredAssets,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    kitId: parseIdentifier(kitUuid),
    version: 0,
    kit: null,
    kitVersion: null,
    space: null,
    spaceOptional: true,
  };
}

export function createUserSettingsNavPoint(match) {
  const { settingsPage } = match.params;
  return {
    type: NavPointTypes.UserSettings,
    settingsPage,
    loaded: false,
  };
}

export function createSpaceBillingNavPoint(match, subdomain) {
  const { spaceId } = match.params;
  return {
    type: NavPointTypes.SpaceBilling,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    space: null,
  };
}

// Space search is only for legacy links
export function createSpaceSearchNavPoint(match, subdomain) {
  const { spaceId } = match.params;
  return {
    type: NavPointTypes.SpaceSearch,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    space: null,
  };
}

export function createSpaceSettingsNavPoint(match, subdomain) {
  const { spaceId, settingsPage } = match.params;
  return {
    type: NavPointTypes.SpaceSettings,
    settingsPage,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    space: null,
  };
}

export function createSpaceMembersNavPoint(match, subdomain) {
  const { spaceId, role } = match.params;
  return {
    type: NavPointTypes.SpaceMembers,
    role,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    space: null,
  };
}

export function createInsightsNavPoint(match, subdomain) {
  const { spaceId, insightsPage } = match.params;
  return {
    type: NavPointTypes.Insights,
    insightsPage,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    space: null,
  };
}

export function createNewSpaceNavPoint() {
  return {
    type: NavPointTypes.NewSpace,
    loaded: false,
  };
}

export function createSpaceKitsNavPoint(match, subdomain) {
  const { spaceId } = match.params;
  return {
    type: NavPointTypes.SpaceKits,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    space: null,
  };
}

export function createKitCollectionNavPoint(match, subdomain) {
  const { spaceId, kitCollectionId } = match.params;
  return {
    type: NavPointTypes.KitCollection,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    kitCollectionId: parseIdentifier(kitCollectionId) || undefined,
    subdomain,
    space: null,
  };
}

export function createDefaultSpaceNavPoint(subdomain) {
  return {
    type: NavPointTypes.DefaultSpace,
    loaded: false,
    subdomain,
  };
}

export function createAssetLibraryNavPoint(match, subdomain) {
  const { spaceId } = match.params;
  return {
    type: NavPointTypes.Library,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    space: null,
  };
}

export function createAssetTagManagementNavPoint(match, subdomain) {
  const { spaceId } = match.params;
  return {
    type: NavPointTypes.TagManagement,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    space: null,
  };
}

export function createLibraryAssetNavPoint(match, subdomain) {
  const { spaceId, assetUuid } = match.params;
  return {
    type: NavPointTypes.LibraryAsset,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    assetId: parseIdentifier(assetUuid) || undefined,
    subdomain,
    space: null,
  };
}

export function createCustomFieldsNavPoint(match, subdomain) {
  const { spaceId } = match.params;
  return {
    type: NavPointTypes.CustomFields,
    loaded: false,
    spaceId: parseInt(spaceId) || undefined,
    subdomain,
    space: null,
  };
}
